import React, { useRef, useState } from "react";
import {
  Pressable,
  StyleSheet,
  TextInput,
  type TextStyle,
  TouchableOpacity,
  View,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import ValidationMessage from "./ValidationMessage";

import { type ValidationError } from "@/schemas/forms/error";
import { isValidPassword } from "@/schemas/forms/password";
import { isiOS } from "@/utils/platform";
import EnvironmentVariablesLoader, { Environment } from "@/utils/environment";

type PasswordInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  iconColor?: string;
};

// NOTE: this is a hack for Maestro (e2e) testing. For some reason,
// The secureText doesn't work on iOS when there are two secure inputs.
function shouldSecureText(): boolean {
  const env = EnvironmentVariablesLoader.getEnvironmentByChannel();
  if (isiOS() && env === Environment.TEST) {
    return false;
  }
  return true;
}

function PasswordInput({
  value,
  onChangeText,
  placeholder,
  containerStyle,
  inputStyle,
  iconColor = "#718096",
}: PasswordInputProps): React.JSX.Element {
  const [isFocused, setIsFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] =
    useState(!shouldSecureText());
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);
  const [showError, setShowError] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const validatePassword = (password: string): ValidationError | null => {
    const validation = isValidPassword(password);
    if (validation.isValid) {
      return null;
    } else {
      const error = { message: validation.message };
      return error;
    }
  };

  const handleFocus = (): void => {
    setIsFocused(true);
    setShowError(true);
  };

  const handleChangeText = (text: string): void => {
    onChangeText(text);
    setValidationError(validatePassword(text));
  };

  const togglePasswordVisibility = (): void => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  return (
    <Pressable onPress={() => inputRef.current?.focus()}>
      <View
        testID="password-input"
        style={[
          styles.container,
          containerStyle,
          isFocused && styles.focusedContainer,
          showError && validationError !== null && styles.invalidContainer,
        ]}
      >
        <TextInput
          ref={inputRef}
          // testID="password-input" // does not work here
          style={[styles.input, inputStyle]}
          value={value}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          placeholder={placeholder}
          secureTextEntry={!isPasswordVisible}
          autoCapitalize={"none"}
          autoCorrect={false}
        />
        <TouchableOpacity
          onPress={togglePasswordVisibility}
          style={styles.iconContainer}
          accessibilityRole="button"
          accessibilityLabel={
            isPasswordVisible ? "Hide password" : "Show password"
          }
        >
          <Ionicons
            name={isPasswordVisible ? "eye-off" : "eye"}
            size={26}
            color={iconColor}
          />
        </TouchableOpacity>
      </View>
      {showError && validationError !== null && (
        <ValidationMessage error={validationError} />
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    backgroundColor: "white",
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  focusedContainer: {
    borderColor: "#4299E1",
  },
  invalidContainer: {
    borderColor: "#F56565",
  },
  input: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 12,
    color: "#2D3748",
  },
  iconContainer: {},
});

export default PasswordInput;
